import './components/calculators/format';
import './components/calculators/finance';
import './components/calculators/calculator-products';
import './components/calculators/mortgage';
import './components/calculators/input';
import './components/calculators/products';

// =====================================================================
//  MORTGAGE REFINANCE + DEBT CONSOLIDATION — interactivity layer
//  All markup lives in mortgage-refinance.html. This file only:
//    1. Reads inputs (debt rows, profile, tenures, cash-out)
//    2. Recomputes totals + per-bank offers
//    3. Toggles visibility and updates text/widths on static elements
// =====================================================================
(function () {
  const fmt = Calc.format;
  const fin = Calc.finance;

  const state = {
    sort: { key: 'best', dir: 'desc' },   // template's selected option sets the default
    debts: {
      mortgage: { enabled: true,  outstanding: 300000, monthly: 2000 },
      // Only the mortgage (the home loan being refinanced) is pre-filled by
      // default; the other debts start collapsed ("+ Add"), no values counted.
      cc:       { enabled: false, outstanding: 100000, monthly: 5000, autoCalc: true },
      pl:       { enabled: false, outstanding: 100000, monthly: 2000 },
      hp:       { enabled: false, outstanding: 50000,  monthly: 900 },
    },
    cashOut: 100000,
    termTenure: 30,
    plCollateralTenure: 10,
    income: 12000,
    age: 35,
    incomeType: 'salaried',
  };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => root.querySelectorAll(sel);
  const setText = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  // Write a value into a placeholder span (text is in HTML, JS only writes the value).
  const setVal = (sel, val, root) => {
    const el = (root || document).querySelector(sel);
    if (el) el.textContent = val;
  };
  const toggle = (sel, hide, root) => {
    const el = (root || document).querySelector(sel);
    if (el) el.classList.toggle('d-none', hide);
  };
  // Escape dynamic strings before injecting as HTML (bank names / reason text
  // originate from the external product feed).
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  // ---------- helpers ----------
  const termLoanAmount = () => state.debts.mortgage.enabled ? state.debts.mortgage.outstanding : 0;
  function plCollateralAmount() {
    let sum = state.cashOut || 0;
    ['cc', 'pl', 'hp'].forEach(k => { if (state.debts[k].enabled) sum += state.debts[k].outstanding; });
    return sum;
  }
  const newLoanTotal = () => termLoanAmount() + plCollateralAmount();
  function existingMonthlyTotal() {
    let sum = 0;
    Object.values(state.debts).forEach(d => { if (d.enabled) sum += +d.monthly || 0; });
    return sum;
  }
  function existingDebtTotal() {
    let sum = 0;
    Object.values(state.debts).forEach(d => { if (d.enabled) sum += +d.outstanding || 0; });
    return sum;
  }
  const netIncome = () => state.income * 0.80;

  // ---------- debt row visibility (toggle .disabled + show right head bits) ----------
  function syncDebtRows() {
    ['mortgage', 'cc', 'pl', 'hp'].forEach(key => {
      const wrap = document.querySelector(`.debt-row-card[data-key="${key}"]`);
      if (!wrap) return;
      const d = state.debts[key];
      wrap.classList.toggle('disabled', !d.enabled);
      // Toggle the enabled vs disabled head bits
      wrap.querySelectorAll('.drc-when-enabled').forEach(el => el.classList.toggle('d-none', !d.enabled));
      wrap.querySelectorAll('.drc-when-disabled').forEach(el => el.classList.toggle('d-none', d.enabled));
      if (d.enabled) {
        const sumOut = wrap.querySelector('.drc-summary-out');
        const sumMon = wrap.querySelector('.drc-summary-mon');
        if (sumOut) sumOut.textContent = fmt.money(d.outstanding, 0);
        if (sumMon) sumMon.textContent = fmt.money(d.monthly, 0);
        const outIn = wrap.querySelector('.rf-out');
        const monIn = wrap.querySelector('.rf-mon');
        Calc.input.syncValue(outIn, d.outstanding);
        Calc.input.syncValue(monIn, (+d.monthly).toFixed(0));
        if (monIn && key === 'cc') monIn.readOnly = !!d.autoCalc;
      }
    });
  }

  // ---------- structure bar ----------
  function updateStructure() {
    const term = termLoanAmount();
    const pl = plCollateralAmount();
    const total = term + pl;
    const termPct = total > 0 ? (term / total * 100) : 0;
    const plPct = 100 - termPct;
    const termBar = $('#rf-bar-term');
    const plBar = $('#rf-bar-pl');
    if (termBar) {
      termBar.style.width = termPct + '%';
      setVal('.rf-bar-term-pct', termPct.toFixed(0));
      toggle('.rf-bar-term-text', termPct < 15);
    }
    if (plBar) {
      plBar.style.width = plPct + '%';
      setVal('.rf-bar-pl-pct', plPct.toFixed(0));
      toggle('.rf-bar-pl-text', plPct < 15);
    }
    setText('rf-amount-term', fmt.money(term, 0));
    setText('rf-amount-pl',   fmt.money(pl, 0));
  }

  // ---------- stat band ----------
  function updateBand(offers) {
    const eligibleSorted = Object.values(offers).filter(o => o.eligible).sort((a, b) => b.savings - a.savings);
    const best = eligibleSorted[0];
    const oldMonthly = existingMonthlyTotal();
    const newMonthly = best ? best.totalMonthly : 0;
    const savings = oldMonthly - newMonthly;

    setText('rf-stat-old', fmt.money(oldMonthly, 0));
    toggle('.rf-stat-new-label-base', !!best);
    toggle('.rf-stat-new-label-bank', !best);
    if (best) setVal('.rf-stat-new-label-bankname', best.bank.name);
    setText('rf-stat-new',   best ? fmt.money(newMonthly, 0) : '—');
    setText('rf-stat-save',  best ? fmt.money(Math.max(0, savings), 0) : '—');
    setText('rf-stat-total', fmt.money(newLoanTotal(), 0));
  }

  // ---------- compute & eligibility ----------
  function buildOffers() {
    const term = termLoanAmount();
    const pl = plCollateralAmount();
    const out = {};
    // Guard against malformed feed data: getCalculatorProducts()/json_encode
    // can hand us a non-array (object or null) when the upstream list is empty
    // or keyed oddly. Treat anything that isn't an array as "no lenders".
    const lenders = Array.isArray(Calc.data?.mortgageRefinance) ? Calc.data.mortgageRefinance : [];
    lenders.forEach(bank => {
      const termCalc = fin.reducingRate(term, bank.interestRate / 100, state.termTenure);
      const plCalc = fin.reducingRate(pl, bank.interestRate / 100, state.plCollateralTenure);
      const totalMonthly = termCalc.monthlyInstalment + plCalc.monthlyInstalment;
      const elig = checkEligibility(bank, totalMonthly);
      out[bank.prod_id] = {
        bank, termCalc, plCalc, totalMonthly,
        savings: existingMonthlyTotal() - totalMonthly,
        eligible: elig.eligible, reasons: elig.reasons,
      };
    });
    return out;
  }

  // Format a list of structured {code, params} reasons into human-readable
  // strings. Mirrors what Calc.eligibility.renderReasons does, but degrades
  // gracefully if that helper is missing on the page: it falls back to the
  // shared template cache (populated by partials/elig-templates.php), and
  // only emits raw codes as a last resort. This keeps the "Not eligible for"
  // panel readable even if the prescreening script fails to load.
  function formatReasons(reasons) {
    if (typeof Calc?.eligibility?.renderReasons === 'function') {
      return Calc.eligibility.renderReasons(reasons);
    }
    const tplCache = Calc?.eligibility?._tplCache || {};
    return reasons.map(r => {
      const tpl = tplCache[r.code];
      if (!tpl) return r.code;
      return tpl.replace(/\{(\w+)\}/g, (_, k) => {
        const v = r.params && r.params[k];
        if (v == null) return '';
        return typeof v === 'number' ? v.toLocaleString() : String(v);
      });
    });
  }

  // Emits structured {code, params} reasons; the host page provides the
  // localized templates (see partials/elig-templates.php).
  function checkEligibility(bank, newMonthly) {
    const e = bank.eligibility;
    const reasons = [];
    // Each rule is opt-in: a null/0/empty value on the eligibility record
    // means "the upstream feed didn't supply this constraint", so we skip
    // the check rather than treating the missing value as a hard zero.
    if (e.minAge && state.age < e.minAge) reasons.push({ code: 'min_age', params: { min: e.minAge } });
    if (e.maxAge && state.age > e.maxAge) reasons.push({ code: 'max_age', params: { max: e.maxAge } });
    if (e.minIncome && state.income < e.minIncome)
      reasons.push({ code: 'min_income', params: { min: e.minIncome } });
    // Filter to non-empty entries first: upstream feeds sometimes deliver
    // [null], [""], or whitespace-only strings to mean "no constraint", but
    // those still satisfy a naive .length check and would render as
    // "(accepts: )" — ugly and misleading. Treat as "constraint absent".
    const validEmpTypes = Array.isArray(e.employmentTypes)
      ? e.employmentTypes.filter(t => t != null && String(t).trim() !== '')
      : [];
    if (validEmpTypes.length && !validEmpTypes.includes(state.incomeType))
      reasons.push({ code: 'employment_not_accepted', params: { accepts: validEmpTypes.join(', ') } });
    if (newLoanTotal() < bank.minAmount)
      reasons.push({ code: 'min_loan', params: { min: bank.minAmount } });
    if (e.maxAge) {
      const yearsToAgeCap = e.maxAge - state.age;
      if (state.termTenure > yearsToAgeCap)
        reasons.push({ code: 'tenure_exceeds_age', params: { yrs: yearsToAgeCap, age: state.age } });
    }
    if (e.dsrTiers?.length) {
      let maxDsr = 0;
      for (const t of e.dsrTiers) {
        if (state.income <= t.incomeUpTo) { maxDsr = t.maxDsr; break; }
      }
      const dsr = state.income > 0 ? newMonthly / state.income : 0;
      if (dsr > maxDsr)
        reasons.push({ code: 'dsr_exceeded', params: { dsr: (dsr * 100).toFixed(0), max: (maxDsr * 100).toFixed(0) } });
    }
    if (e.minNDIUrban) {
      const ndi = netIncome() - newMonthly;
      if (ndi < e.minNDIUrban)
        reasons.push({ code: 'ndi_below_min', params: { ndi: ndi.toFixed(0), min: e.minNDIUrban } });
    }
    return { eligible: reasons.length === 0, reasons };
  }

  // ---------- bank cards ----------
  function updateBanks(offers) {
    const eligibleAll = Object.values(offers).filter(o => o.eligible);
    const bestSorted = eligibleAll.slice().sort((a, b) => b.savings - a.savings);
    const bestId = (bestSorted.length && bestSorted[0].savings > 0) ? bestSorted[0].bank.prod_id : null;
    const eligibleSorted = Calc.products.rank(
      eligibleAll.map((o, i) => ({ offer: o, index: i, name: o.bank.name, metrics: { best: o.savings } })),
      state.sort.key, state.sort.dir
    ).map(e => e.offer);
    const ineligibleReasons = [];

    let eligibleCount = 0;
    const wraps = $$('.bank-card-wrap');
    // Keyed on the element, not the id: feed prod_id is a STRING while dataset
    // reads coerce to a number, and `===` between them silently never matched.
    const wrapOf = new Map();
    wraps.forEach(wrap => {
      const rawId = wrap.dataset.bankId;
      const offer = offers[rawId];
      if (!offer) return;
      if (!offer.eligible) {
        wrap.classList.add('d-none');
        const rendered = formatReasons(offer.reasons);
        ineligibleReasons.push(`<strong>${esc(offer.bank.name)}:</strong> ${esc(rendered.join('; '))}`);
        return;
      }
      eligibleCount++;
      wrap.classList.remove('d-none');
      wrapOf.set(String(rawId), wrap);

      const logo = wrap.querySelector('.bank-logo');
      if (logo && offer.bank.image_url && logo.src !== offer.bank.image_url) {
        logo.src = offer.bank.image_url;
        logo.alt = offer.bank.name;
      }

      wrap.querySelector('.bank-monthly').textContent = fmt.money(offer.totalMonthly, 0);

      const isPositive = offer.savings >= 0;
      const strip = wrap.querySelector('.bank-save-strip');
      strip.classList.toggle('is-negative', !isPositive);
      const lblPos = wrap.querySelector('.bank-save-label-pos');
      const lblNeg = wrap.querySelector('.bank-save-label-neg');
      lblPos.classList.toggle('d-none', !isPositive);
      lblNeg.classList.toggle('d-none', isPositive);
      [lblPos, lblNeg].forEach(el => {
        el.classList.toggle('text-primary', isPositive);
        el.classList.toggle('text-danger', !isPositive);
      });
      const val = wrap.querySelector('.bank-save-value');
      val.textContent = fmt.money(Math.abs(offer.savings), 0);
      val.classList.toggle('text-primary', isPositive);
      val.classList.toggle('text-danger', !isPositive);

    });

    Calc.products.applyOrder(
      wraps,
      eligibleSorted.map(o => ({ wrap: wrapOf.get(String(o.bank.prod_id)) })).filter(e => e.wrap),
      bestId != null ? wrapOf.get(String(bestId)) : null
    );

    const emptyState = $('#rf-banks .rf-empty-state');
    if (emptyState) {
      emptyState.classList.toggle('d-none', eligibleCount > 0);
      emptyState.style.order = wraps.length;
    }

    const note = $('#rf-ineligible');
    const reasonsEl = $('#rf-ineligible-reasons');
    if (ineligibleReasons.length) {
      note.classList.remove('d-none');
      reasonsEl.innerHTML = ineligibleReasons.join('\n');
    } else {
      note.classList.add('d-none');
    }
  }

  // ---------- refresh ----------
  function refresh() {
    syncDebtRows();
    setText('rf-existing-out', fmt.money(existingDebtTotal()));
    setText('rf-existing-mon', fmt.money(existingMonthlyTotal()));
    updateStructure();
    const offers = buildOffers();
    updateBand(offers);
    updateBanks(offers);
  }

  // ---------- events ----------
  function bindEvents() {
    document.addEventListener('input', (e) => {
      const t = e.target;

      if (t.classList.contains('rf-out') || t.classList.contains('rf-mon')) {
        const card = t.closest('.debt-row-card');
        const key = card.dataset.key;
        const d = state.debts[key];
        const v = Calc.input.getValue(t);
        if (t.classList.contains('rf-out')) {
          d.outstanding = v || 0;
          if (key === 'cc' && d.autoCalc) {
            d.monthly = fin.creditCardMinimum(d.outstanding);
            const monIn = card.querySelector('.rf-mon');
            Calc.input.syncValue(monIn, (+d.monthly).toFixed(0));
          }
        } else {
          d.monthly = v || 0;
        }
        refresh();
        return;
      }

      const map = {
        'rf-cashout':            v => state.cashOut = v || 0,
        'rf-income':             v => state.income = v || 0,
        'rf-age':                v => state.age = v || 0,
        'rf-term-tenure-input':  v => state.termTenure = v || 1,
        'rf-pl-tenure-input':    v => state.plCollateralTenure = v || 1,
      };
      if (map[t.id]) {
        map[t.id](Calc.input.getValue(t));
        refresh();
      }
    });

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t.id === 'rf-itype') { state.incomeType = t.value; refresh(); }
      if (t.classList.contains('rf-cc-auto')) {
        const d = state.debts.cc;
        d.autoCalc = t.checked;
        if (t.checked) d.monthly = fin.creditCardMinimum(d.outstanding);
        refresh();
      }
    });

    document.addEventListener('click', (e) => {
      const row = e.target.closest('.debt-row-card');
      if (row) {
        const act = e.target.closest('[data-act]')?.dataset.act;
        const key = row.dataset.key;
        if (act === 'enable' && !state.debts[key].enabled) {
          state.debts[key].enabled = true;
          if (key === 'cc' && state.debts.cc.autoCalc) {
            state.debts.cc.monthly = fin.creditCardMinimum(state.debts.cc.outstanding);
          }
          refresh();
          return;
        }
        if (act === 'disable') {
          state.debts[key].enabled = false;
          refresh();
          return;
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindEvents();
    const initialSort = Calc.products.bindSort('#rf-sort-select', (sort) => {
      state.sort = sort;
      refresh();
    });
    if (initialSort) state.sort = initialSort;
    refresh();
  });
})();